// Analytics Dashboard Component
import React from 'react';
import { getCampaignDaysRemaining } from '../../data/campaignModels';

const AnalyticsDashboard = ({ campaign }) => {
  const daysRemaining = getCampaignDaysRemaining(campaign);
  const analytics = campaign.analytics || {};
  const dailyStats = analytics.dailyStats || [];

  const totalViews = analytics.views || 0;
  const totalClicks = analytics.clicks || 0;
  const ctr = totalViews > 0 ? (totalClicks / totalViews * 100) : 0;
  const uniqueViews = analytics.uniqueViews || 0;
  const uniqueClicks = analytics.uniqueClicks || 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600 mb-1">Total Views</div>
          <div className="text-2xl font-bold text-blue-600">{totalViews.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">Unique: {uniqueViews.toLocaleString()}</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600 mb-1">Total Clicks</div>
          <div className="text-2xl font-bold text-green-600">{totalClicks.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">Unique: {uniqueClicks.toLocaleString()}</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600 mb-1">CTR</div>
          <div className="text-2xl font-bold text-purple-600">{ctr.toFixed(2)}%</div>
          <div className="text-xs text-gray-500 mt-1">Click-through Rate</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600 mb-1">Days Remaining</div>
          <div className="text-2xl font-bold text-orange-600">
            {daysRemaining !== null ? daysRemaining : '∞'}
          </div>
          <div className="text-xs text-gray-500 mt-1">วัน</div>
        </div>
      </div>

      {/* Daily Stats Table */}
      {dailyStats.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="font-semibold">Daily Statistics</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">วันที่</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicks</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CTR</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unique Views</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unique Clicks</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dailyStats.slice().reverse().map((stat, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(stat.date).toLocaleDateString('th-TH')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {stat.views || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {stat.clicks || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {stat.ctr ? stat.ctr.toFixed(2) : '0.00'}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {stat.uniqueViews || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {stat.uniqueClicks || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Export Options */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="font-semibold mb-4">Export Report</h3>
        <div className="flex space-x-4">
          <button
            onClick={() => {
              // TODO: Implement PDF export
              alert('PDF Export กำลังพัฒนา...');
            }}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
          >
            Export PDF
          </button>
          <button
            onClick={() => {
              // TODO: Implement Excel export
              alert('Excel Export กำลังพัฒนา...');
            }}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            Export Excel
          </button>
          <button
            onClick={() => {
              // Export as CSV
              const csv = [
                ['Date', 'Views', 'Clicks', 'CTR', 'Unique Views', 'Unique Clicks'],
                ...dailyStats.map(stat => [
                  stat.date,
                  stat.views || 0,
                  stat.clicks || 0,
                  stat.ctr ? stat.ctr.toFixed(2) : '0.00',
                  stat.uniqueViews || 0,
                  stat.uniqueClicks || 0
                ])
              ].map(row => row.join(',')).join('\n');
              
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `banner-analytics-${campaign.id}.csv`;
              a.click();
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Export CSV
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;

